import {
  ButtonDS,
  DeleteOutlined,
  LaunchPage,
  LockOpenOutlined,
  LockOutlined,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  Switch,
  TextCapitalized,
  TogglesGroupCell,
  triggerToastDS,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  companyFollowUpAsync,
  companyFollowUpSlice,
  createCompanyExternalIbanAsync,
  selectCompanyFollowUp,
  selectCompanyFollowUpStatus,
  selectCreateCompanyAcronymStatus,
  selectCreateCompanyExternalIbanStatus,
  selectDeleteCompanyStatus,
  selectUpdateCompanyAnalyticsStatus,
  selectUpdateCompanyHasGacStatus,
  selectUpdateCompanyAutoApproveDriverAccessRequestsStatus,
  selectUpdateCompanyHasGeolocationStatus,
  selectUpdateCompanyHasLogbookSessionsStatus,
  selectUpdateCompanyHomeChargingStatus,
  selectUpdateCompanyStatusStatus,
  updateCompanyAnalyticsAsync,
  updateCompanyHomeChargingAsync,
  updateCompanyHasGacAsync,
  updateCompanyAutoApproveDriverAccessRequestsAsync,
  updateCompanyHasGeolocationAsync,
  updateCompanyHasLogbookSessionsAsync,
} from "./followUpSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { CompanyFollowUp, CompanyFollowUpDisplayed } from "./followUpApi";
import { useTranslation } from "react-i18next";
import {
  ExternalIbanModal,
  ExternalIbanModalState,
} from "../modals/externalIbanModal";
import { ExternalIbanInput } from "./followUpInputs/externalIbanInput";
import { AcronymInput } from "./followUpInputs/acronymInput";
import "../../i18n/i18n";
import { selectAdminUser } from "../homeSlice";
import { UpdateCompanyStatusModal } from "./modals/updateCompanyStatusModal";

const CONTRACTS_DRIVE_FOLDER_URL =
  "https://drive.google.com/drive/folders/1Wj5sCmlpSNsbDAQQfYvRcYWenF-o14dz";

export const CompanyFollowUpTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companyFollowUpList = useAppSelector(selectCompanyFollowUp);
  const companyFollowUpListStatus = useAppSelector(selectCompanyFollowUpStatus);
  const createCompanyAcronymStatus = useAppSelector(
    selectCreateCompanyAcronymStatus,
  );
  const createCompanyExternalIbanStatus = useAppSelector(
    selectCreateCompanyExternalIbanStatus,
  );
  const deleteCompanyStatus = useAppSelector(selectDeleteCompanyStatus);
  const updateCompanyStatusStatus = useAppSelector(
    selectUpdateCompanyStatusStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    CompanyFollowUpDisplayed[] | null
  >(null);
  const adminUser = useAppSelector(selectAdminUser);
  const updateCompanyHasGacStatus = useAppSelector(
    selectUpdateCompanyHasGacStatus,
  );
  const updateCompanyAutoApproveDriverAccessRequestsStatus = useAppSelector(
    selectUpdateCompanyAutoApproveDriverAccessRequestsStatus,
  );
  const updateCompanyHasGeolocationStatus = useAppSelector(
    selectUpdateCompanyHasGeolocationStatus,
  );
  const updateCompanyHasLogbookSessionsStatus = useAppSelector(
    selectUpdateCompanyHasLogbookSessionsStatus,
  );
  const updateCompanyAnalyticsStatus = useAppSelector(
    selectUpdateCompanyAnalyticsStatus,
  );
  const updateCompanyHomeChargingStatus = useAppSelector(
    selectUpdateCompanyHomeChargingStatus,
  );

  const [modalState, setModalState] = useState<ExternalIbanModalState>({
    isVisible: false,
  });
  const [companyToUpdateStatus, setCompanyToUpdateStatus] = useState<{
    uuid: string;
    name: string;
    status: CompanyFollowUpDisplayed["status"];
  } | null>(null);

  const handleModalVisibility = useCallback(
    (visible: boolean, iban?: string, companyUuid?: string, name?: string) => {
      setModalState({
        isVisible: visible,
        ...(visible ? { iban, companyUuid, name } : {}),
      });
    },
    [],
  );

  const handleConfirmExternalIban = useCallback(() => {
    setModalState({
      isVisible: false,
    });
    if (modalState.companyUuid && modalState.iban) {
      void dispatch(
        createCompanyExternalIbanAsync({
          companyUuid: modalState.companyUuid,
          externalIban: modalState.iban,
        }),
      );
    }
  }, [modalState, dispatch]);

  const handleUpdateAutoApproveDriverAccessRequests = useCallback(
    (companyUuid: string, autoApproveDriverAccessRequests: boolean) => {
      if (!adminUser?.email) return;
      void dispatch(
        updateCompanyAutoApproveDriverAccessRequestsAsync({
          companyUuid,
          autoApproveDriverAccessRequests,
          updatedBy: adminUser.email,
        }),
      );
    },
    [adminUser?.email, dispatch],
  );

  const handleUpdateCompanyHasLogbookSessions = useCallback(
    (companyUuid: string, currentHasLogbookSessions: boolean) => {
      if (!adminUser?.email) return;
      void dispatch(
        updateCompanyHasLogbookSessionsAsync({
          companyUuid,
          hasLogbookSessions: !currentHasLogbookSessions,
          updatedBy: adminUser.email,
        }),
      );
    },
    [adminUser?.email, dispatch],
  );

  const handleUpdateCompanyHomeCharging = useCallback(
    (companyUuid: string, hasHomeCharging: boolean, name: string) => {
      if (!adminUser?.email) return;
      void dispatch(
        updateCompanyHomeChargingAsync({
          companyUuid,
          hasHomeCharging,
          name,
          updatedBy: adminUser.email,
        }),
      );
    },
    [adminUser?.email, dispatch],
  );

  const authorizedAdmin = useMemo(() => {
    return [
      "p.chopin@qivia.fr",
      "m.gatinois@qivia.fr",
      "m.banjac@qivia.fr",
      "a.chagra@qivia.fr",
      "t.bouffard@qivia.fr",
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const isAdminUserKnown = useMemo(() => !!adminUser?.email, [adminUser]);

  const env = import.meta.env["VITE_TARGET_ENV"] as string;
  const isProduction = env === "production";

  const pageName = "companyFollowUp";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "avg",
      merchantName: "size",
      category: "size",
      driverName: "size",
    },
  };

  const findKeysToTranslate = useCallback(
    (key: keyof CompanyFollowUp, value: string) => {
      switch (key) {
        case "status":
          return t(`companyFollowUp.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status"] as (keyof CompanyFollowUpDisplayed)[],
    [],
  );

  const companyListWithId = useMemo(() => {
    const rowsWithId = companyFollowUpList.map((cd) => {
      let newDict: CompanyFollowUp & { id: string } = {
        ...cd,
        id: cd.companyUuid,
      };
      Object.entries(cd).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof CompanyFollowUpDisplayed)) {
          newDict = {
            ...newDict,
            [key]: findKeysToTranslate(
              key as keyof CompanyFollowUp,
              value as string,
            ),
          };
        }
      });
      return newDict;
    });
    return rowsWithId;
  }, [companyFollowUpList, findKeysToTranslate, keysToTranslate]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: companyListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const isAcronymValid = useCallback(
    (input: string) =>
      companyFollowUpList.reduce(
        (acc, curr) => acc && curr.acronym !== input,
        true,
      ),
    [companyFollowUpList],
  );

  useEffect(() => {
    if (createCompanyAcronymStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.acronym.create.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (createCompanyAcronymStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.acronym.create.failure") || "",
        "ERROR",
      );
    }
  }, [createCompanyAcronymStatus, t, dispatch]);

  useEffect(() => {
    if (createCompanyExternalIbanStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.externalIban.create.success") || "",
        "SUCCESS",
      );
      dispatch(
        companyFollowUpSlice.actions.resetCreateCompanyExternalIbanStatus(),
      );
    } else if (createCompanyExternalIbanStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.externalIban.create.failure") || "",
        "ERROR",
      );
    }
  }, [createCompanyExternalIbanStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGacStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasGac.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGacStatus === "failed") {
      triggerToastDS(t("companyFollowUp.hasGac.update.failure") || "", "ERROR");
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGacStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyAutoApproveDriverAccessRequestsStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.autoApproveDriverAccessRequests.update.success") ||
          "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
      void dispatch(companyFollowUpAsync());
    } else if (
      updateCompanyAutoApproveDriverAccessRequestsStatus === "failed"
    ) {
      triggerToastDS(
        t("companyFollowUp.autoApproveDriverAccessRequests.update.failure") ||
          "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyAutoApproveDriverAccessRequestsStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGeolocationStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasGeolocation.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGeolocationStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.hasGeolocation.update.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGeolocationStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasLogbookSessionsStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasLogbookSessions.update.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasLogbookSessionsStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.hasLogbookSessions.update.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasLogbookSessionsStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyAnalyticsStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.hasAnalytics.update.success") || "",
        "SUCCESS",
      );
      void dispatch(companyFollowUpAsync());
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyAnalyticsStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.hasAnalytics.update.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyAnalyticsStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHomeChargingStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.homeCharging.update.success") || "",
        "SUCCESS",
      );
      dispatch(
        companyFollowUpSlice.actions.resetUpdateCompanyHomeChargingStatus(),
      );
      dispatch(companyFollowUpSlice.actions.resetCompanyFollowUpStatus());
    } else if (updateCompanyHomeChargingStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.homeCharging.update.failure") || "",
        "ERROR",
      );
      dispatch(
        companyFollowUpSlice.actions.resetUpdateCompanyHomeChargingStatus(),
      );
    }
  }, [updateCompanyHomeChargingStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyStatusStatus === "success") {
      triggerToastDS(
        t("companyFollowUp.updateCompanyStatus.success") || "",
        "SUCCESS",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateCompanyStatusStatus());
      dispatch(companyFollowUpSlice.actions.resetCompanyFollowUpStatus());
    } else if (updateCompanyStatusStatus === "failed") {
      triggerToastDS(
        t("companyFollowUp.updateCompanyStatus.failure") || "",
        "ERROR",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateCompanyStatusStatus());
    }
  }, [updateCompanyStatusStatus, t, dispatch]);

  useEffect(() => {
    if (deleteCompanyStatus === "success") {
      triggerToastDS(t("companyFollowUp.delete.success") || "", "SUCCESS");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
      dispatch(companyFollowUpSlice.actions.resetCompanyFollowUpStatus());
    } else if (deleteCompanyStatus === "failed") {
      triggerToastDS(t("companyFollowUp.delete.failure") || "", "ERROR");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
    }
  }, [deleteCompanyStatus, t, dispatch]);

  useEffect(() => {
    if (companyFollowUpListStatus === "idle") {
      void dispatch(companyFollowUpAsync());
    }
  }, [companyFollowUpListStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof CompanyFollowUpDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 200,
      },
      balance: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{Math.round(value / 100)} €</> : <>N/A</>;
        },
      },
      acronym: {
        type: "string",
        width: 300,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <AcronymInput
              isAcronymValid={isAcronymValid}
              companyUuid={props.row["companyUuid"] as string}
            />
          );
        },
      },
      externalIban: {
        type: "string",
        width: 360,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <ExternalIbanInput
              setIsExternalIbanModalVisible={(
                visible: boolean,
                iban?: string,
              ) =>
                handleModalVisibility(
                  visible,
                  iban,
                  props.row["companyUuid"] as string,
                  props.row["name"] as string,
                )
              }
            />
          );
        },
      },
      hasGac: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={props.row["hasGac"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGacAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGac: !(props.row["hasGac"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </SwitchCellContainer>
          );
        },
      },
      hasLogbookSessions: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={!!props.row["hasLogbookSessions"]}
                size={"S"}
                onClick={() =>
                  handleUpdateCompanyHasLogbookSessions(
                    props.row["companyUuid"] as string,
                    !!props.row["hasLogbookSessions"],
                  )
                }
                disabled={!isAdminUserKnown}
              />
            </SwitchCellContainer>
          );
        },
      },
      hasGeolocation: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={props.row["hasGeolocation"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGeolocationAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGeolocation: !(props.row["hasGeolocation"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </SwitchCellContainer>
          );
        },
      },
      autoApproveDriverAccessRequests: {
        type: "string",
        width: 320,
        renderCell: (props) => {
          return (
            <SwitchCellContainer>
              <Switch
                checked={!!props.row["autoApproveDriverAccessRequests"]}
                size={"S"}
                onClick={() =>
                  handleUpdateAutoApproveDriverAccessRequests(
                    props.row["companyUuid"] as string,
                    !props.row["autoApproveDriverAccessRequests"],
                  )
                }
              />
            </SwitchCellContainer>
          );
        },
      },
      hasAnalytics: {
        type: "string",
        width: 320,
        renderCell: (props) => {
          return (
            <TogglesGroupCell
              primaryToggle={{
                checked: props.row["hasAdvancedAnalytics"] as boolean,
                onClick: () => {
                  const toggled = !(props.row[
                    "hasAdvancedAnalytics"
                  ] as boolean);
                  void dispatch(
                    updateCompanyAnalyticsAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasAnalytics: toggled,
                      hasAdvancedAnalytics: toggled,
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                },
              }}
              secondaryToggles={[
                {
                  label: t("companyFollowUp.packages.homeCharging"),
                  checked: props.row["hasHomeCharging"] as boolean,
                  disabled: isProduction,
                  onClick: () =>
                    handleUpdateCompanyHomeCharging(
                      props.row["companyUuid"] as string,
                      !props.row["hasHomeCharging"],
                      props.row["name"] as string,
                    ),
                },
              ]}
            />
          );
        },
      },
      status: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      updateCompanyStatus: {
        type: "string",
        width: 220,
        valueFormatter: (
          value: string | null,
          row: CompanyFollowUpDisplayed & { id: string },
        ) => {
          if (row["status"] === t("companyFollowUp.status.DELETE")) {
            return <></>;
          }
          const isLocked = row["status"] === t("companyFollowUp.status.LOCKED");
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: isLocked ? <LockOpenOutlined /> : <LockOutlined />,
                  size: "L",
                }}
                buttonType={isLocked ? "secondary" : "primary"}
                format={"fill"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  setCompanyToUpdateStatus({
                    uuid: row["id"],
                    name: row["name"],
                    status: isLocked ? "ACTIVE" : "LOCKED",
                  });
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );
        },
      },
      deleteCompany: {
        type: "string",
        width: 220,
        valueFormatter: (
          value: string | null,
          row: CompanyFollowUpDisplayed & { id: string },
        ) => {
          if (row["status"] === t("companyFollowUp.status.DELETE")) {
            return (
              <TextCapitalized>
                {t("companyFollowUp.status.alreadyDeleted")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <DeleteOutlined />,
                  size: "L",
                }}
                buttonType="danger"
                format={"fill"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  setCompanyToUpdateStatus({
                    uuid: row["id"],
                    name: row["name"],
                    status: "DELETE",
                  });
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, [
    isAcronymValid,
    handleModalVisibility,
    dispatch,
    adminUser?.email,
    authorizedAdmin,
    handleUpdateAutoApproveDriverAccessRequests,
    handleUpdateCompanyHasLogbookSessions,
    handleUpdateCompanyHomeCharging,
    isAdminUserKnown,
    isProduction,
    t,
  ]);

  useEffect(() => {
    if (companyListWithId && companyFollowUpListStatus === "success") {
      setListDisplayed(companyListWithId);
    }
  }, [companyListWithId, companyFollowUpListStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (
    params.tab !== "companyFollowUp" ||
    adminUser?.email === "exterieur1@qivia.fr"
  ) {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <a href={CONTRACTS_DRIVE_FOLDER_URL} target="_blank" rel="noreferrer">
        {t("companyFollowUp.contractsDriveLink")}
      </a>
      <Spacer y={1} />
      <ExternalIbanModal
        visible={modalState.isVisible}
        onClose={() => setModalState({ isVisible: false })}
        selectedIban={modalState.iban}
        selectedName={modalState.name}
        onConfirm={handleConfirmExternalIban}
        modalType="ADD"
      />
      <MaterialUiTable<keyof CompanyFollowUpDisplayed, CompanyFollowUpDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      <UpdateCompanyStatusModal
        visible={!!companyToUpdateStatus}
        onClose={() => setCompanyToUpdateStatus(null)}
        company={companyToUpdateStatus}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SwitchCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
